import { useMemo, useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const RevealLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Starfield = () => {
  const stars = useMemo(
    () =>
      Array.from({ length: 90 }, (_, i) => ({
        left: `${(i * 37.7) % 100}%`,
        top: `${(i * 53.3) % 100}%`,
        size: (i % 3) + 1,
        delay: `${(i % 7) * 0.6}s`,
        duration: `${3 + (i % 5)}s`,
      })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      {stars.map((s, i) => (
        <span
          key={i}
          className="absolute animate-twinkle rounded-full bg-cyan-100"
          style={{ left: s.left, top: s.top, width: s.size, height: s.size, animationDelay: s.delay, animationDuration: s.duration }}
        />
      ))}
    </div>
  );
};

const PulseDot = () => (
  <span className="relative flex h-2 w-2">
    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-60" />
    <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-300" />
  </span>
);

const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(my, { stiffness: 90, damping: 16 });
  const rotateY = useSpring(mx, { stiffness: 90, damping: 16 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yEarth = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const handleMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 12);
    my.set(-((e.clientY - r.top) / r.height - 0.5) * 12);
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      onMouseMove={handleMouse}
      className="relative flex min-h-[100svh] items-center overflow-hidden"
    >
      <Starfield />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(#16233d_1px,transparent_1px)] opacity-30 [background-size:26px_26px]" />
      <div className="pointer-events-none absolute -left-40 top-1/4 h-96 w-96 rounded-full bg-cyan-500/15 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[28rem] w-[28rem] rounded-full bg-blue-700/20 blur-3xl" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-5 pb-32 pt-32 sm:px-8 lg:grid-cols-12 lg:pb-24 lg:pt-40">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.div
            data-testid="hero-eyebrow-badge"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="inline-flex items-center gap-3 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-2"
          >
            <PulseDot />
            <span className="font-mono text-[10px] tracking-[0.3em] text-cyan-300 sm:text-[11px]">
              COMING SOON — LAUNCHING ONLINE
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="mt-8 font-display text-[13vw] font-extrabold leading-[0.95] tracking-tight sm:text-7xl lg:text-[5.2rem] xl:text-8xl"
          >
            <RevealLine delay={0.25}>
              <span data-testid="hero-line-1">STORIES THAT</span>
            </RevealLine>
            <RevealLine delay={0.38}>
              <span data-testid="hero-line-2" className="text-outline">
                MATTER.
              </span>
            </RevealLine>
            <RevealLine delay={0.51}>
              <span data-testid="hero-line-3" className="brand-gradient-text font-editorial font-normal italic tracking-normal">
                Told Openly.
              </span>
            </RevealLine>
          </h1>

          <motion.p
            data-testid="hero-intro-text"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-8 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base"
          >
            The Open Tale is a new digital news &amp; media brand. We are already sharing stories on social
            media — our full website is on its way. Real stories. A brighter tomorrow.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-follow-cta"
              onClick={() => scrollTo("#follow")}
              className="group inline-flex items-center gap-2 rounded-full bg-cyan-400 px-7 py-3.5 font-display text-sm font-bold tracking-wide text-ink transition-colors duration-300 hover:bg-cyan-300"
            >
              Follow the journey
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-coverage-cta"
              onClick={() => scrollTo("#coverage")}
              className="group inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 font-display text-sm font-bold tracking-wide text-slate-200 transition-colors duration-300 hover:border-cyan-400/50 hover:text-cyan-300"
            >
              Explore coverage
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-12 font-mono text-[10px] tracking-[0.3em] text-slate-500 sm:text-xs"
          >
            THEOPENTALE.COM <span className="mx-2 text-cyan-500">✦</span> 07 EDITORIAL DESKS{" "}
            <span className="mx-2 text-cyan-500">✦</span> EST. 2026
          </motion.p>
        </motion.div>

        <div className="lg:col-span-5" style={{ perspective: 1200 }}>
          <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative">
            <motion.div
              data-testid="hero-earth-frame"
              style={{ y: yEarth }}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.7, ease: EASE }}
              className="relative overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_120px_rgba(6,182,212,0.15)]"
            >
              <img
                src="/assets/hero-earth.jpg"
                alt="Earth at night from space — A more informed tomorrow"
                className="h-64 w-full object-cover sm:h-80 lg:h-[26rem]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
              <span className="absolute bottom-4 right-4 rounded-full border border-white/15 bg-ink/60 px-3 py-1.5 font-mono text-[10px] tracking-[0.25em] text-cyan-200 backdrop-blur">
                DISPATCH 000 — TRANSMISSION SOON
              </span>
            </motion.div>
            <div className="absolute -bottom-12 -left-4 sm:-left-8 lg:-left-14" style={{ transform: "translateZ(70px)" }}>
              <motion.img
                data-testid="hero-logo-visual"
                src="/assets/logo.png"
                alt="The Open Tale circular globe logo"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1, y: [0, -12, 0] }}
                transition={{
                  opacity: { duration: 0.9, delay: 1.1 },
                  scale: { duration: 0.9, delay: 1.1, ease: EASE },
                  y: { duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 },
                }}
                className="w-32 drop-shadow-[0_0_45px_rgba(6,182,212,0.5)] sm:w-44 lg:w-52"
              />
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        data-testid="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-3 md:flex"
      >
        <span className="font-mono text-[10px] tracking-[0.4em] text-slate-500">SCROLL</span>
        <motion.span
          animate={{ scaleY: [0.2, 1, 0.2], opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="block h-10 w-px origin-top bg-gradient-to-b from-cyan-400 to-transparent"
        />
      </motion.div>
    </section>
  );
};
